/**
 * Team Bios Config Panel Component
 *
 * Allows Owner/Manager to configure team bios display settings:
 * - Page title
 * - Name display format
 * - Sort order
 * - External URL for back navigation
 * - Embed code
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  Divider,
} from '@mui/material';
import { Palette as PaletteIcon } from '@mui/icons-material';
import { useUpdateTeamBioConfigMutation } from '../../../api/teamBiosApi';
import type {
  TeamBioConfig,
  NameDisplayFormat,
  SortOrder,
} from '../../../types/teamBios';

interface TeamBiosConfigPanelProps {
  studioId: string;
  config: TeamBioConfig;
}

const TeamBiosConfigPanel: React.FC<TeamBiosConfigPanelProps> = ({
  studioId,
  config,
}) => {
  const navigate = useNavigate();
  const [updateConfig, { isLoading, error }] = useUpdateTeamBioConfigMutation();

  // Form state
  const [title, setTitle] = useState(config.title || 'Team Bios');
  const [nameFormat, setNameFormat] = useState<NameDisplayFormat>(
    config.name_display_format
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(config.sort_order);
  const [externalUrl, setExternalUrl] = useState(config.external_url);

  // Update form when config changes
  useEffect(() => {
    setTitle(config.title || 'Team Bios');
    setNameFormat(config.name_display_format);
    setSortOrder(config.sort_order);
    setExternalUrl(config.external_url);
  }, [config]);

  // Handle save and continue to edit members
  const handleSaveAndContinue = async () => {
    try {
      // Check if there are unsaved changes
      const hasChanges =
        title !== config.title ||
        nameFormat !== config.name_display_format ||
        sortOrder !== config.sort_order ||
        externalUrl !== config.external_url;

      // Only save if there are changes
      if (hasChanges) {
        await updateConfig({
          studioId,
          data: {
            title,
            name_display_format: nameFormat,
            sort_order: sortOrder,
            external_url: externalUrl,
          },
        }).unwrap();
      }

      // Navigate to Edit Members view
      navigate(`/team-bios/edit/${studioId}`);
    } catch (err) {
      console.error('Failed to update config:', err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configuration
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error">
            Failed to save configuration. Please try again.
          </Alert>
        )}

        {/* Page Title */}
        <TextField
          label="Page Title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          fullWidth
          placeholder="Team Bios"
          helperText="Custom title for your team bios page (defaults to 'Team Bios')"
        />

        {/* Name Display Format */}
        <TextField
          label="Name Display Format"
          value={nameFormat}
          onChange={e => setNameFormat(e.target.value as NameDisplayFormat)}
          select
          fullWidth
          helperText="How team member names should be displayed"
        >
          <MenuItem value="full">Full Name (John Smith)</MenuItem>
          <MenuItem value="first_only">First Name Only (John)</MenuItem>
          <MenuItem value="first_last_initial">
            First Name + Last Initial (John S.)
          </MenuItem>
        </TextField>

        {/* Sort Order */}
        <TextField
          label="Sort Order"
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value as SortOrder)}
          select
          fullWidth
          helperText="Default order for displaying team members"
        >
          <MenuItem value="first_name">First Name (Alphabetical)</MenuItem>
          <MenuItem value="last_name">Last Name (Alphabetical)</MenuItem>
          <MenuItem value="role">Role</MenuItem>
          <MenuItem value="custom">Custom Order</MenuItem>
        </TextField>

        {/* External URL */}
        <TextField
          label="External URL"
          value={externalUrl}
          onChange={e => setExternalUrl(e.target.value)}
          fullWidth
          placeholder="https://example.com"
          helperText="Optional URL to link back to your main website"
        />

        {/* Theme Customization Section */}
        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            Appearance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Customize colors, fonts, and branding for your public Team Bios page
          </Typography>
          <Button
            variant="outlined"
            startIcon={<PaletteIcon />}
            onClick={() => navigate(`/theme`)}
          >
            Customize Theme
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Save & Continue Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSaveAndContinue}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Continue to Edit Members'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TeamBiosConfigPanel;
